import React, { useEffect, useState } from'react';
import { useForm } from'react-hook-form';
import { DynamicForm } from'@shared/components/DynamicForm';
import { FormField } from'@shared/services/FormServices';
import { Calendar } from'lucide-react';
import { StepHelpers } from'@shared/components/Stepper';
import { SelectedAccountsBanner } from '../../../../../shared/components/SelectedAccountsBanner';
import { useCurrency } from'@shared/selectors/globalSelectors';
import { FIELD_CLASSES } from '@shared/utils/constants';
import { Button } from '@shared/ui/button';
import { useUtilityRequestConfiguration } from '../../../../../shared/hook';
import { getRemoteUtilityId } from '@shared/utils/getUtilityId';
import { useTranslation } from 'react-i18next';
import { useTimeSlotChoices } from '../../../../../requests/reconnect/hooks';

// Category color mapping function
const getCategoryColor = (category: string) => {
 const cat = category?.toLowerCase() ||'';
 if (cat.includes('water') && !cat.includes('waste')) return'bg-blue-600 text-white';
 if (cat.includes('waste') || cat.includes('sewer')) return'bg-emerald-500 text-white';
 if (cat.includes('electric')) return'bg-yellow-500 text-white';
 return'bg-purple-600 text-white';
};

export const ServiceDetailsStep = ({
 stepHelpers,
 currentStepIndex = 1,
 onNext,
 onPrevious,
 requestType ="Service"
}: {
 stepHelpers?: StepHelpers;
 currentStepIndex?: number;
 onNext?: () => void;
 onPrevious?: () => void;
 remoteUtilityId?: number;
 requestType?: string;
}) => {
 const { t } = useTranslation();
 const form = useForm({
  defaultValues: stepHelpers?.getStepData(currentStepIndex) || {}
 });
 const currency = useCurrency();

 const selectedId = stepHelpers?.getStepData(currentStepIndex - 1)?.selectedId;
 const { data: configData, isLoading } = useUtilityRequestConfiguration({
  remote_utility_id: getRemoteUtilityId(),
  requestType: requestType,
  disablePagination: true,
 });

 const selectedService = (configData?.result || []).find(s => s.id?.toString() === selectedId);
 console.log("selectedService", selectedService)
 const category = selectedService?.supportRequestTypeDisplay || selectedService?.requestTypeDisplay || 'Service';
 const price = selectedService?.extraData?.serviceCharge ? `${currency} ${selectedService.extraData.serviceCharge}` : t("Free");
 const {data:timeslots}=useTimeSlotChoices();
 useEffect(() => {
  const subscription = form.watch((value) => {
   if (stepHelpers) {
    stepHelpers.setStepData(currentStepIndex, value);
   }
  });
  return () => subscription.unsubscribe();
 }, [form, stepHelpers, currentStepIndex]);

 const fields: FormField[] = [
  {
   name: 'preferredDate',
   label: t("Preferred Date (Optional)"),
   type: 'datepicker',
   required: false,
  },
  {
   name: 'preferredTime',
   label: t("Preferred Time Slot (Optional)"),
   type: 'select',
   required: false,
   options:  timeslots?.result?.map((slot) => ({
   label: slot.value,
   value: slot.key.toString(),
  })) || [],
  },
  {
   name: 'instructions',
   label: t("Additional Instructions (Optional)"),
   type: 'textarea',
   required: false,
   rows: 4,
   className: 'col-span-full',
   placeholder: t("Include any special requirements, access instructions, or specific requests")
  }
 ];

 return (
 <div className="space-y-6">
  <SelectedAccountsBanner stepHelpers={stepHelpers} />
  <div>
  <h3 className="text-lg font-semibold text-gray-900">{t("Service Details")}</h3>
  <p className="text-sm text-gray-500">{t("Review selected service and configure scheduling preferences")}</p>
  </div>

  <div className="space-y-2">
  <h4 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
   {t("Selected Service")}
  </h4>
   <div className="border border-gray-200 rounded-xl p-5 bg-white">
    <div className="flex justify-between items-start mb-4">
     <div>
      <div className="text-[10px] font-semibold text-gray-400 tracking-wider mb-1">{t("Service Name")}</div>
      <div className="font-semibold text-gray-900 mb-2">{selectedService?.name}</div>
     </div>
     <div className="text-right">
      <div className="text-[10px] font-semibold text-gray-400 tracking-wider mb-1">{t("Utility Service")}</div>
      <div className="text-2xl font-bold text-blue-600 leading-none mb-1">
       {selectedService?.productCode && selectedService?.productCode.length > 0 ? selectedService?.productCode.join(', ') : 'NA'}
      </div>
     </div>
     <div className="text-right">
      <div className="text-[10px] font-semibold text-gray-400 tracking-wider mb-1">{t("Service Charges")}</div>
      <div className="text-2xl font-bold text-blue-600 leading-none mb-1">{price}</div>
      <div className="text-xs text-gray-500">{t("Base service fee")}</div>
     </div>
    </div>
    
    <div>
    <div className="text-[10px] font-semibold text-gray-400 tracking-wider mb-1">{t("Description")}</div>
    <p className="text-sm text-blue-600">{selectedService?.longDescription || t("No description available.")}</p>
    </div>
   </div>
  </div>

  <div className="pt-4">
   <DynamicForm form={form} fields={fields} gridColumns={2}  config={{classes:FIELD_CLASSES ,showSubmitButton: false,
            showResetButton: false,}} />
  </div>

  <div className="flex justify-between pt-6 border-t border-gray-200">
  <Button
   onClick={onPrevious}
   variant="outline"
   className="px-6 py-2.5 text-sm font-medium"
  >
   {t("Previous")}
  </Button>
  <Button
   onClick={onNext}
   className="px-6 py-2.5 text-sm font-medium bg-blue-600 hover:bg-blue-700 text-white"
  >
   {t("Continue")}
  </Button>
  </div>
 </div>
 );
};
